import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CalendarDays, CalendarRange, Link as LinkIcon, UserPlus } from "lucide-react";
import { supabaseV2 } from "../../lib/supabase";
import { usePermissions } from "../../lib/permissions";
import { addDays, romeDate, todayRome, useSeason } from "../../lib/seasons";
import { AlertBar } from "../AlertBar";
import type { View } from "./nav-config";

// Dismiss persistito in localStorage. La chiave puo' cambiare (es. include il
// conteggio): quando cambia, l'effetto ri-legge -> la barra ricompare se la
// situazione e' cambiata (nuovo membro in attesa).
function useDismissed(key: string): [boolean, () => void] {
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    try {
      setDismissed(window.localStorage.getItem(key) === "1");
    } catch {
      setDismissed(false);
    }
  }, [key]);
  const dismiss = () => {
    try {
      window.localStorage.setItem(key, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };
  return [dismissed, dismiss];
}

/**
 * Barre di notifica in cima all'app per le cose importanti e azionabili.
 * Renderizzata nella shell sopra il contenuto della vista. Founder/coach only.
 * Ogni barra e' dismissibile.
 */
export function AppAlertBars({
  teamId,
  activeView,
  onNavigate,
}: {
  teamId: string;
  activeView: View;
  onNavigate: (view: View) => void;
}) {
  const { t } = useTranslation("shell");
  const { canManage, canManageMembers } = usePermissions();

  // Membri entrati (es. via invite link) ancora senza profilo di rosa collegato.
  const unlinkedQuery = useQuery({
    queryKey: ["alert-unlinked-members", teamId],
    enabled: canManage,
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("list_unlinked_team_members", {
        _team_id: teamId,
      });
      if (error) throw error;
      return (data || []).length as number;
    },
  });
  const unlinkedCount = unlinkedQuery.data || 0;

  // Numero di player non-guest in rosa: serve a decidere se ha ancora senso
  // mostrare la barra invite (sopra una certa soglia la rosa e' avviata e
  // il link generico non e' utile).
  const rosterCountQuery = useQuery({
    queryKey: ["alert-roster-count", teamId],
    enabled: canManageMembers,
    queryFn: async () => {
      const { count, error } = await supabaseV2
        .from("players")
        .select("id", { count: "exact", head: true })
        .eq("team_id", teamId)
        .eq("is_guest", false)
        .eq("status", "active");
      if (error) throw error;
      return count || 0;
    },
    staleTime: 60_000,
  });
  const rosterCount = rosterCountQuery.data ?? null;

  // Esiste almeno una sessione per il team? Serve per decidere la barra
  // "Crea prima sessione" (compare quando la rosa c'è ma le sessioni no).
  const sessionsCountQuery = useQuery({
    queryKey: ["alert-sessions-count", teamId],
    enabled: canManage,
    queryFn: async () => {
      const { count, error } = await supabaseV2
        .from("sessions")
        .select("id", { count: "exact", head: true })
        .eq("team_id", teamId);
      if (error) throw error;
      return count || 0;
    },
    staleTime: 60_000,
  });
  const sessionsCount = sessionsCountQuery.data ?? null;

  // Chiave dismiss della barra "membri da agganciare": include il conteggio,
  // cosi' se ne arriva uno nuovo la barra ricompare.
  const unlinkedKey = `eb-bar:unlinked:${teamId}:${unlinkedCount}`;
  const inviteKey = `eb-bar:invite:${teamId}`;
  const firstSessionKey = `eb-bar:first-session:${teamId}`;

  // Nudge di fine stagione per il founder: la stagione in corso dura da piu'
  // di 3 mesi e c'e' stata una pausa lunga di recente (ripartenza in vista).
  const { activeSeason, isViewingClosed } = useSeason();
  const seasonSessionsQuery = useQuery({
    queryKey: ["alert-season-sessions", teamId, activeSeason?.id],
    enabled: canManageMembers && !!activeSeason,
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("sessions")
        .select("starts_at")
        .eq("team_id", teamId)
        .eq("season_id", activeSeason!.id)
        .order("starts_at");
      if (error) throw error;
      return (data || []).map((row: { starts_at: string }) => romeDate(row.starts_at));
    },
    staleTime: 5 * 60_000,
  });
  const seasonCloseKey = `eb-bar:season-close:${activeSeason?.id || "none"}`;
  const [seasonCloseDismissed, dismissSeasonClose] = useDismissed(seasonCloseKey);
  const suggestSeasonClose = (() => {
    if (!activeSeason || !seasonSessionsQuery.data) return false;
    const today = todayRome();
    if (activeSeason.starts_on > addDays(today, -90)) return false;
    const past = seasonSessionsQuery.data.filter((d) => d <= today);
    const recentFrom = addDays(today, -60);
    for (let i = 0; i < past.length; i += 1) {
      const until = past[i + 1] ?? today;
      if (until < recentFrom) continue;
      const gapDays = (new Date(`${until}T12:00:00Z`).getTime() - new Date(`${past[i]}T12:00:00Z`).getTime()) / 86_400_000;
      if (gapDays >= 21) return true;
    }
    return false;
  })();
  const [unlinkedDismissed, dismissUnlinked] = useDismissed(unlinkedKey);
  const [inviteDismissed, dismissInvite] = useDismissed(inviteKey);
  const [firstSessionDismissed, dismissFirstSession] = useDismissed(firstSessionKey);

  const bars: ReactNode[] = [];

  // 1) Membri da agganciare a un profilo di rosa. Non sulla vista Richieste
  //    (dove l'azione si svolge gia').
  if (canManage && unlinkedCount > 0 && !unlinkedDismissed && activeView !== "requests") {
    bars.push(
      <AlertBar
        key="unlinked"
        tone="warning"
        icon={<UserPlus className="h-4 w-4" />}
        actionLabel={t("alertBars.unlinked.action")}
        onAction={() => onNavigate("requests")}
        onDismiss={dismissUnlinked}
      >
        {t("alertBars.unlinked.message", { count: unlinkedCount })}
      </AlertBar>,
    );
  }

  // 2) Nudge: condividi il link di invito per far crescere la squadra.
  //    Non in Home (lì c'è già la card invito) e solo se la rosa è ancora
  //    piccola (<5 player non-guest). Sopra quella soglia il link generico
  //    non è utile (i player importati vanno collegati via player-link request).
  //    Aspettiamo il primo fetch della count: niente == null, per evitare il
  //    flash della barra prima di sapere quanti player ci sono.
  if (
    canManageMembers
    && !inviteDismissed
    && activeView !== "home"
    && rosterCount !== null
    && rosterCount < 5
  ) {
    bars.push(
      <AlertBar
        key="invite"
        tone="info"
        icon={<LinkIcon className="h-4 w-4" />}
        actionLabel={t("alertBars.invite.action")}
        onAction={() => onNavigate("home")}
        onDismiss={dismissInvite}
      >
        {t("alertBars.invite.message")}
      </AlertBar>,
    );
  }

  // 3) Nudge: crea la prima sessione. Compare quando la rosa è avviata
  //    (>=1 player non-guest) ma non c'è ancora nessuna sessione. Non sulle
  //    view Home (c'è già la FirstSessionCard) e Sessions (ci sei già).
  if (
    canManage
    && !firstSessionDismissed
    && activeView !== "home"
    && activeView !== "sessions"
    && rosterCount !== null
    && rosterCount >= 1
    && sessionsCount === 0
  ) {
    bars.push(
      <AlertBar
        key="first-session"
        tone="info"
        icon={<CalendarDays className="h-4 w-4" />}
        actionLabel={t("alertBars.firstSession.action")}
        onAction={() => onNavigate("sessions")}
        onDismiss={dismissFirstSession}
      >
        {t("alertBars.firstSession.message")}
      </AlertBar>,
    );
  }

  // 4) Fine stagione: suggerisce la chiusura guidata (solo founder).
  if (
    canManageMembers
    && suggestSeasonClose
    && !seasonCloseDismissed
    && !isViewingClosed
    && activeView !== "seasons"
    && activeSeason
  ) {
    bars.push(
      <AlertBar
        key="season-close"
        tone="info"
        icon={<CalendarRange className="h-4 w-4" />}
        actionLabel={t("alertBars.seasonClose.action")}
        onAction={() => onNavigate("seasons")}
        onDismiss={dismissSeasonClose}
      >
        {t("alertBars.seasonClose.message", { name: activeSeason.name })}
      </AlertBar>,
    );
  }

  if (bars.length === 0) return null;
  return <div className="px-4 sm:px-6 pt-3 max-w-[1600px] w-full mx-auto space-y-2">{bars}</div>;
}
